import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"
import { Loader2 } from "lucide-react"

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40",
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        // 28px: dense rows, card toolbars, side panels and image overlays, between xs and sm.
        compact: "h-7 gap-1.5 rounded-md px-2.5 has-[>svg]:px-2",
        sm: "h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-compact": "size-7",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
      /**
       * Disables the button and shows a spinner in place of its icon (or before its label when it
       * has none), so `{saving ? <Loader2 … /> : <Save />}` isn't needed.
       */
      loading?: boolean
      /** Shown in place of the label while loading ("Saving…"), so the label needs no ternary. */
      loadingText?: React.ReactNode
      /**
       * Why the button is disabled ("Add who it goes to"), shown on hover and keyboard focus while
       * it is disabled and not loading. `data-hint` stays for what the button does. Ignored with
       * asChild.
       */
      disabledReason?: string | null
      /**
       * Classes that place the button in its row ("flex-1 sm:flex-none", "max-sm:ml-auto"). They go
       * on the wrapper while a reason shows, otherwise on the button.
       */
      layoutClassName?: string
    }
>(function Button(
  {
    className,
    variant = "default",
    size = "default",
    asChild = false,
    loading = false,
    loadingText,
    disabled,
    disabledReason,
    layoutClassName,
    children,
    ...props
  },
  ref
) {
  const Comp = asChild ? Slot.Root : "button"
  const showSpinner = loading && !asChild
  const baseId = React.useId()
  const reason = !asChild && disabled && !loading && disabledReason ? disabledReason : null
  const reasonId = `${baseId}reason`
  const buttonId = props.id ?? `${baseId}button`
  // With a reason the wrapper below stands in for the button with screen readers, so the button
  // itself is hidden from them and isn't read twice.
  const wrapped = reason ? { id: buttonId, "aria-hidden": true } : null

  const button = (
    <Comp
      ref={ref}
      data-slot="button"
      data-variant={variant}
      data-size={size}
      data-loading={loading || undefined}
      aria-busy={loading || undefined}
      disabled={asChild ? disabled : disabled || loading}
      className={cn(
        buttonVariants({ variant, size, className: cn(reason ? "grow" : layoutClassName, className) }),
        showSpinner && "[&>svg:not([data-slot=spinner])]:hidden"
      )}
      {...props}
      {...wrapped}
    >
      {showSpinner ? (
        <>
          {/* No size class, so it takes the button size's icon size (size-3 on xs). */}
          <Loader2 data-slot="spinner" aria-hidden className="animate-spin" />
          {loadingText ?? children}
        </>
      ) : (
        children
      )}
    </Comp>
  )
  if (!reason) return button

  // A disabled button gets no pointer or focus events, so the reason sits on a wrapper that does:
  // HintLayer shows it on hover and on Tab. To a screen reader the wrapper is the button: its role,
  // its name (the button's text or aria-label), dimmed, and the reason from the hidden span.
  return (
    <span
      data-slot="button-disabled-reason"
      data-hint={reason}
      role="button"
      aria-disabled="true"
      aria-labelledby={props["aria-labelledby"] ?? buttonId}
      aria-describedby={[props["aria-describedby"], reasonId].filter(Boolean).join(" ")}
      tabIndex={0}
      className={cn(
        "inline-flex shrink-0 cursor-not-allowed rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
        layoutClassName
      )}
    >
      {button}
      <span id={reasonId} hidden>
        {reason}
      </span>
    </span>
  )
})
Button.displayName = "Button"

export { Button, buttonVariants }
